import type { IChartDataSignal } from "../../scheme/components/analytics/interfaces";
import {
  getOrbitDftFrequencyBoundsHz,
  synthesizeOrbitFromBandpassHz,
  synthesizeOrbitFromSelectedBinCenters,
} from "../../scheme/components/analytics/utils/orbit-harmonics";

export interface ISignalFilterComponent {
  binIndex: number;
  harmonic: number;
  amplitude: number;
}

const getSignalValues = (signal: IChartDataSignal): number[] =>
  signal.items.map(item => (typeof item.y === "number" ? item.y : item.y.real ?? 0));

export const isSynchronousSignal = (signal: IChartDataSignal | undefined): boolean =>
  signal?.rangeType === 2 || signal?.unitsXCode === 6;

export const estimateSignalSamplingRateHz = (signal: IChartDataSignal | undefined): number | null => {
  if (!signal || signal.items.length < 2) {
    return null;
  }

  const firstX = signal.items[0].x;
  const lastX = signal.items[signal.items.length - 1].x;
  const step = (lastX - firstX) / (signal.items.length - 1);
  if (!Number.isFinite(step) || step <= 0) {
    return null;
  }

  if (signal.unitsXCode === 5) {
    return 1000 / step;
  }
  if (isSynchronousSignal(signal)) {
    return 1 / step;
  }
  return null;
};

export const getSignalFilterFrequencyBounds = (
  signal: IChartDataSignal | undefined,
): { fMin: number; fMax: number } | null => {
  const samplingRateHz = estimateSignalSamplingRateHz(signal);
  return samplingRateHz == null ? null : getOrbitDftFrequencyBoundsHz(signal?.items.length ?? 0, samplingRateHz);
};

export const getSignalFilterComponents = (signal: IChartDataSignal | undefined): ISignalFilterComponent[] => {
  if (!signal || signal.items.length < 4) {
    return [];
  }

  const samplingRateHz = estimateSignalSamplingRateHz(signal);
  const rotationFrequencyHz = isSynchronousSignal(signal) ? 1 : (signal.rotationFrequencyRpm ?? 0) / 60;
  if (samplingRateHz == null || rotationFrequencyHz <= 0) {
    return [];
  }

  const values = getSignalValues(signal);
  const n = values.length;
  const mean = values.reduce((sum, value) => sum + value, 0) / n;
  const peak = values.reduce((max, value) => Math.max(max, Math.abs(value - mean)), 0);
  if (peak < 1e-15) {
    return [];
  }

  const components: ISignalFilterComponent[] = [];
  const threshold = peak * 0.01;
  const maxHarmonic = Math.min(100, Math.floor(samplingRateHz / 2 / rotationFrequencyHz));
  const usedBins = new Set<number>();
  for (let harmonic = 1; harmonic <= maxHarmonic; harmonic += 1) {
    const binIndex = Math.round((harmonic * rotationFrequencyHz * n) / samplingRateHz);
    if (binIndex <= 0 || binIndex >= Math.floor(n / 2) || usedBins.has(binIndex)) {
      continue;
    }
    usedBins.add(binIndex);
    const omega = (2 * Math.PI * binIndex) / n;
    let real = 0;
    let imaginary = 0;
    for (let sampleIndex = 0; sampleIndex < n; sampleIndex += 1) {
      const value = values[sampleIndex] - mean;
      real += value * Math.cos(omega * sampleIndex);
      imaginary += value * Math.sin(omega * sampleIndex);
    }
    const amplitude = (2 * Math.hypot(real, imaginary)) / n;
    if (amplitude >= threshold) {
      components.push({
        binIndex,
        harmonic,
        amplitude,
      });
    }
  }
  return components;
};

const filterSignalSeries = (
  signal: IChartDataSignal,
  harmonics: readonly number[] = [],
  bandpassHz: { lo: number; hi: number } | null = null,
): IChartDataSignal => {
  const samplingRateHz = estimateSignalSamplingRateHz(signal);
  if (samplingRateHz == null) {
    return signal;
  }

  const values = getSignalValues(signal);
  let filteredValues: number[] | null = null;

  if (bandpassHz) {
    filteredValues = synthesizeOrbitFromBandpassHz(values, values, samplingRateHz, bandpassHz.lo, bandpassHz.hi).x;
  } else if (harmonics.length > 0) {
    const rotationFrequencyHz = isSynchronousSignal(signal) ? 1 : (signal.rotationFrequencyRpm ?? 0) / 60;
    if (rotationFrequencyHz <= 0) {
      return signal;
    }
    const bins = harmonics.map(harmonic =>
      Math.round((harmonic * rotationFrequencyHz * values.length) / samplingRateHz),
    );
    filteredValues = synthesizeOrbitFromSelectedBinCenters(
      values,
      values,
      bins,
      values.length,
      1,
      "revolutionsPerBuffer",
    ).x;
  }

  return filteredValues
    ? {
        ...signal,
        items: signal.items.map((item, index) => ({ ...item, y: filteredValues[index] })),
      }
    : signal;
};

export const filterSignalChartData = (
  data: readonly IChartDataSignal[],
  harmonics: readonly number[] = [],
  bandpassHz: { lo: number; hi: number } | null = null,
): IChartDataSignal[] => data.map(signal => filterSignalSeries(signal, harmonics, bandpassHz));
